import type { Song, SongListEntry } from 'types';

/**
 * The fields dropped from a Song to make a SongListEntry.
 *
 * `parts`, `beats` and `arrangement` are the per-bar playback data — `beats` alone is one float
 * per beat of the whole song — and together they are the bulk of a song document. Measured against
 * the dev catalogue they are 80% of it, `beats` alone 64%. Only the player rendering one open song
 * ever touches them, so the cached catalogue keeps them out of memory and out of IndexedDB, and
 * `getSongById` fetches the full document on demand instead. `credits` goes the same way: nothing
 * in the app reads it.
 *
 * This lives here rather than in `types.tsx` so it — and everything below — can be imported and
 * tested without pulling in `fb`, which stands up a real Firebase app at module scope. `types.tsx`
 * derives `SongListEntry` from this list, so the type and the list cannot drift apart.
 */
export const SONG_LIST_OMITTED_FIELDS = ['parts', 'beats', 'arrangement', 'credits'] as const;

/**
 * Drops the playback data from a song document.
 *
 * The kept fields are picked into a fresh object rather than copied-and-deleted: `delete` moves a
 * V8 object into dictionary mode, and every filter, sort and search would then read these songs
 * through the slow path, over and over, for the life of the catalogue.
 */
export const toListEntry = (song: Song): SongListEntry => {
  const omitted = SONG_LIST_OMITTED_FIELDS as readonly string[];
  const entry: Record<string, unknown> = {};
  for (const key of Object.keys(song)) {
    if (!omitted.includes(key)) {
      entry[key] = song[key as keyof Song];
    }
  }
  return entry as unknown as SongListEntry;
};

/**
 * Inserts into a Map used as a least-recently-used cache, evicting from the front until it fits.
 *
 * Map iterates in insertion order, so re-inserting a key that is already present is what moves it
 * to the back — hence the delete before the set. Without it a repeatedly read key would keep its
 * original position and get evicted despite being the hottest entry.
 */
export function rememberInLruCache<T>(cache: Map<string, T>, key: string, value: T, maxSize: number) {
  cache.delete(key);
  cache.set(key, value);
  while (cache.size > maxSize) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) {
      break;
    }
    cache.delete(oldest);
  }
  return cache;
}
